/**
 * Training Engine & State Machine for Aim Assist Training Lab
 * Controls modes, rounds, countdowns, adaptive difficulty, and metric calculation
 */

import { TRAINING_MODES, TRAINING_PROGRAMS, GAME_PRESETS } from './constants.js';
import { analyzeSessionPerformance } from './benchmarks-coaching.js';
import { trackEvent } from '../../core/analytics.js';

export class TrainingEngine {
  constructor(targetManager, shootingSystem, weaponController, audioManager, settingsStore, sessionHistory) {
    this.targetManager = targetManager;
    this.shootingSystem = shootingSystem;
    this.weaponController = weaponController;
    this.audioManager = audioManager;
    this.settingsStore = settingsStore;
    this.sessionHistory = sessionHistory;

    // Drill State
    this.state = 'idle'; // 'idle' | 'countdown' | 'active' | 'paused' | 'finished'
    this.currentMode = 'precision';
    this.currentProgram = 'free_drill';
    this.programStageIndex = 0;

    // Time & Clocks
    this.roundDuration = 60; // seconds
    this.timeRemaining = 60;
    this.countdownSeconds = 3;
    this.countdownTimer = 0;
    this.sessionStartTime = 0;

    // Adaptive Difficulty State
    this.adaptiveLevel = 1;
    this.streak = 0;
    this.targetScaleModifier = 1.0;
    this.targetSpeedModifier = 1.0;
    this.adaptiveMessage = '';
    this.adaptiveMsgTimer = 0;

    // Reaction Mode Specific State
    this.reactionState = 'idle'; // 'waiting_cue' | 'cue_active'
    this.reactionWaitTimer = 0;
    this.reactionCueTime = 0;
    this.currentReactionCue = null;

    // Session Metrics
    this.metrics = {
      score: 0,
      rawScore: 0,
      shots: 0,
      hits: 0,
      misses: 0,
      bullseyes: 0,
      accuracy: 0,
      falseStarts: 0,
      reactionTimes: [],
      acquisitionTimes: [],
      recentShots: [],
      timeOnTargetMs: 0,
      trackingPct: 0,
      switchTransitionTimes: [],
      counterStrafeHits: 0
    };

    // Callback handlers for UI integration
    this.onTick = null;
    this.onStateChange = null;
    this.onFinished = null;
    this.onAdaptiveChange = null;
  }

  start(mode = 'precision', programId = 'free_drill', customDuration = 60) {
    trackEvent('tool_started', 'aim-assist-training');
    this.currentMode = mode;
    this.currentProgram = programId;
    this.programStageIndex = 0;

    const program = TRAINING_PROGRAMS[programId];
    if (program && program.stages) {
      const stage = program.stages[0];
      this.currentMode = stage.mode;
      this.roundDuration = stage.duration;
    } else {
      this.roundDuration = customDuration;
    }

    this.timeRemaining = this.roundDuration;
    this.resetMetrics();
    this.targetManager.clear();
    this.weaponController.reset();

    // Reset adaptive scaling
    this.adaptiveLevel = 1;
    this.targetScaleModifier = 1.0;
    this.targetSpeedModifier = 1.0;

    // Initiate 3-second countdown
    this.state = 'countdown';
    this.countdownSeconds = 3;
    this.countdownTimer = 1.0;
    this.audioManager.playCountdown(false);
    this.notifyState();
  }

  pause() {
    if (this.state === 'active') {
      this.state = 'paused';
      this.notifyState();
    }
  }

  resume() {
    if (this.state === 'paused') {
      this.state = 'active';
      this.notifyState();
    }
  }

  restart() {
    this.start(this.currentMode, this.currentProgram, this.roundDuration);
  }

  resetMetrics() {
    this.metrics = {
      score: 0,
      rawScore: 0,
      shots: 0,
      hits: 0,
      misses: 0,
      bullseyes: 0,
      accuracy: 0,
      falseStarts: 0,
      reactionTimes: [],
      acquisitionTimes: [],
      recentShots: [],
      timeOnTargetMs: 0,
      trackingPct: 0,
      switchTransitionTimes: [],
      counterStrafeHits: 0
    };
    this.streak = 0;
  }

  beginActiveRound() {
    this.state = 'active';
    this.sessionStartTime = performance.now();
    this.audioManager.playCountdown(true); // High tone start beep

    if (this.currentMode === 'reaction') {
      this.scheduleNextReactionCue();
    } else if (this.currentMode === 'switching') {
      this.spawnSwitchingBatch();
    } else {
      this.spawnNextTarget();
    }

    this.notifyState();
  }

  spawnNextTarget() {
    const gamePreset = GAME_PRESETS[this.settingsStore.get('selectedGame')] || GAME_PRESETS.valorant;
    const baseScale = (gamePreset.targetScale || 1.0) * this.targetScaleModifier;
    const baseSpeed = (gamePreset.targetSpeed || 1.0) * this.targetSpeedModifier;

    if (this.currentMode === 'precision') {
      this.targetManager.spawnTarget({
        type: 'static',
        scale: baseScale,
        speed: baseSpeed
      });
    } else if (this.currentMode === 'tracking') {
      this.targetManager.spawnTarget({
        type: 'tracking',
        scale: baseScale * 1.15,
        speed: baseSpeed
      });
    } else if (this.currentMode === 'movement') {
      this.targetManager.spawnTarget({
        type: 'lateral',
        scale: baseScale,
        speed: baseSpeed * 1.2
      });
    }
  }

  spawnSwitchingBatch() {
    const gamePreset = GAME_PRESETS[this.settingsStore.get('selectedGame')] || GAME_PRESETS.valorant;
    const baseScale = (gamePreset.targetScale || 1.0) * this.targetScaleModifier;
    const baseSpeed = (gamePreset.targetSpeed || 1.0) * this.targetSpeedModifier;

    this.targetManager.clear();
    const positions = [
      { x: -4.5, y: 1.8, z: -12 },
      { x: -1.5, y: 2.4, z: -14 },
      { x: 1.8, y: 1.4, z: -11 },
      { x: 4.2, y: 2.2, z: -13 }
    ];

    positions.forEach((pos) => {
      this.targetManager.spawnTarget({
        type: 'switching',
        x: pos.x,
        y: pos.y,
        z: pos.z,
        scale: baseScale * 0.9,
        speed: baseSpeed
      });
    });
  }

  scheduleNextReactionCue() {
    this.targetManager.clear();
    this.reactionState = 'waiting_cue';
    // Randomized interval between 1.4s and 3.8s to eliminate anticipation
    this.reactionWaitTimer = 1.4 + Math.random() * 2.4;
    this.currentReactionCue = null;
    this.notifyTick();
  }

  triggerReactionCue() {
    this.reactionState = 'cue_active';
    this.reactionCueTime = performance.now();

    // Cue variations: visual quadrant direction or action
    const cues = ['left', 'center', 'right', 'high'];
    const selectedCue = cues[Math.floor(Math.random() * cues.length)];
    this.currentReactionCue = selectedCue;

    let x = 0;
    let y = 1.65;
    if (selectedCue === 'left') x = -4.5;
    if (selectedCue === 'right') x = 4.5;
    if (selectedCue === 'high') y = 2.8;

    this.targetManager.spawnTarget({
      type: 'reaction',
      x,
      y,
      z: -12,
      scale: 1.05 * this.targetScaleModifier,
      direction: selectedCue
    });

    this.notifyTick();
  }

  handlePlayerFire(currentTime, movementSpeed = 0, isCounterStrafing = true, isAirborne = false, sprayOffset = null) {
    if (this.state !== 'active') return false;

    // Reaction Mode false start detection
    if (this.currentMode === 'reaction') {
      if (this.reactionState === 'waiting_cue') {
        // Player fired before cue was presented — FALSE START!
        this.metrics.falseStarts++;
        this.metrics.rawScore = Math.max(0, (this.metrics.rawScore || 0) - 150);
        this.updateAccuracy();
        this.audioManager.playFalseStart();
        this.scheduleNextReactionCue();
        this.notifyTick();
        return false;
      }
    }

    const result = this.shootingSystem.processFire(currentTime, movementSpeed, isCounterStrafing, isAirborne, sprayOffset);
    if (!result) return false; // Weapon could not fire (out of ammo or reloading)

    this.metrics.shots++;

    if (result.hit && result.target) {
      this.metrics.hits++;
      this.streak++;

      // Speed bonus & reaction tracking
      const timeToHit = currentTime - result.target.spawnTime;
      this.metrics.acquisitionTimes.push(timeToHit);

      if (!this.metrics.recentShots) this.metrics.recentShots = [];
      this.metrics.recentShots.unshift({
        type: result.isBullseye ? 'bullseye' : 'hit',
        latency: Math.round(timeToHit),
        timestamp: currentTime
      });
      if (this.metrics.recentShots.length > 8) this.metrics.recentShots.pop();

      // Distance and Target Scale Normalization
      const targetDist = Math.abs(result.target.mesh?.position.z || -12);
      const targetScale = result.target.targetScale || 1.0;
      const distMultiplier = Math.max(0.5, Math.min(2.4, targetDist / 10));
      const sizeMultiplier = Math.max(0.6, Math.min(2.0, 1.0 / targetScale));
      const basePoints = 100 * distMultiplier * sizeMultiplier;

      // Bullseye / Headshot multiplier: 2.2x
      let hitScore = result.isBullseye ? Math.round(basePoints * 2.2) : Math.round(basePoints);
      if (result.isBullseye) {
        this.metrics.bullseyes++;
      }

      // Speed bonus: faster flick = higher score
      if (timeToHit < 250) {
        hitScore += 50;
      } else if (timeToHit < 400) {
        hitScore += 35;
      } else if (timeToHit < 650) {
        hitScore += 18;
      }

      // Counter-strafing reward in movement mode
      if (this.currentMode === 'movement') {
        if (movementSpeed < 0.25 || isCounterStrafing) {
          this.metrics.counterStrafeHits++;
          hitScore += 35;
        }
      }

      // Streak Multiplier: every 5 streak adds 10% bonus, up to +50%
      const streakBonus = Math.min(0.5, Math.floor(this.streak / 5) * 0.1);
      hitScore = Math.round(hitScore * (1 + streakBonus));

      this.metrics.rawScore = (this.metrics.rawScore || 0) + hitScore;

      // Reaction mode cue resolution
      if (this.currentMode === 'reaction') {
        const reactionLatency = currentTime - this.reactionCueTime;
        this.metrics.reactionTimes.push(reactionLatency);
        this.targetManager.destroyTarget(result.target, result.isBullseye);
        this.scheduleNextReactionCue();
      } else {
        this.targetManager.destroyTarget(result.target, result.isBullseye);

        if (this.currentMode === 'switching') {
          if (this.targetManager.getActiveCount() === 0) {
            this.audioManager.playTargetDestroy();
            this.spawnSwitchingBatch();
          }
        } else if (this.currentMode !== 'tracking') {
          this.spawnNextTarget();
        }
      }

      this.checkAdaptiveDifficulty();
    } else {
      // Missed shot
      this.metrics.misses++;
      this.streak = 0;
      const missPenalty = 45;
      this.metrics.rawScore = Math.max(0, (this.metrics.rawScore || 0) - missPenalty);

      if (!this.metrics.recentShots) this.metrics.recentShots = [];
      this.metrics.recentShots.unshift({
        type: 'miss',
        latency: 0,
        timestamp: currentTime
      });
      if (this.metrics.recentShots.length > 8) this.metrics.recentShots.pop();

      this.checkAdaptiveDifficulty();
    }

    this.updateAccuracy();
    this.notifyTick();
    return true;
  }

  updateAccuracy() {
    if (this.metrics.shots > 0) {
      this.metrics.accuracy = Math.round((this.metrics.hits / this.metrics.shots) * 1000) / 10;
    }
    if (this.currentMode !== 'tracking') {
      const raw = this.metrics.rawScore || 0;
      if (this.metrics.shots > 0) {
        const accRatio = Math.max(0, this.metrics.hits / this.metrics.shots);
        // Exponential accuracy weighting: (Accuracy %)^1.35
        const accMultiplier = Math.pow(accRatio, 1.35);
        this.metrics.score = Math.max(0, Math.round(raw * accMultiplier));
      } else {
        this.metrics.score = 0;
      }
    }
  }

  getAverageReactionTime() {
    if (this.metrics.acquisitionTimes && this.metrics.acquisitionTimes.length > 0) {
      const sum = this.metrics.acquisitionTimes.reduce((a, b) => a + b, 0);
      return Math.round(sum / this.metrics.acquisitionTimes.length);
    }
    if (this.metrics.reactionTimes && this.metrics.reactionTimes.length > 0) {
      const sum = this.metrics.reactionTimes.reduce((a, b) => a + b, 0);
      return Math.round(sum / this.metrics.reactionTimes.length);
    }
    return 0;
  }

  checkAdaptiveDifficulty() {
    if (!this.settingsStore.get('adaptiveDifficulty')) return;

    if (this.streak >= 5 && this.metrics.accuracy >= 80) {
      // Step up difficulty
      this.adaptiveLevel++;
      this.targetScaleModifier = Math.max(0.7, this.targetScaleModifier * 0.93);
      this.targetSpeedModifier = Math.min(1.4, this.targetSpeedModifier * 1.08);
      this.streak = 0;
      this.showAdaptiveNotice(`مستوى الصعوبة ارتفع إلى ${this.adaptiveLevel} (+سرعة، -حجم)`);
    } else if (this.metrics.shots >= 10 && this.metrics.accuracy < 55 && this.adaptiveLevel > 1) {
      // Step down difficulty to recalibrate motor accuracy
      this.adaptiveLevel--;
      this.targetScaleModifier = Math.min(1.3, this.targetScaleModifier * 1.08);
      this.targetSpeedModifier = Math.max(0.8, this.targetSpeedModifier * 0.92);
      this.showAdaptiveNotice(`إعادة ضبط الصعوبة إلى مستوى ${this.adaptiveLevel}`);
    }
  }

  showAdaptiveNotice(msg) {
    this.adaptiveMessage = msg;
    this.adaptiveMsgTimer = 2.2;
    if (this.onAdaptiveChange) this.onAdaptiveChange(msg);
  }

  update(delta, currentTime) {
    if (this.state === 'countdown') {
      this.countdownTimer -= delta;
      if (this.countdownTimer <= 0) {
        this.countdownSeconds--;
        if (this.countdownSeconds <= 0) {
          this.beginActiveRound();
        } else {
          this.countdownTimer = 1.0;
          this.audioManager.playCountdown(false);
          this.notifyTick();
        }
      }
      return;
    }

    if (this.state !== 'active') return;

    // 1. Clock Tick
    this.timeRemaining -= delta;
    if (this.timeRemaining <= 0) {
      this.timeRemaining = 0;
      this.finishRound();
      return;
    }

    // 2. Continuous Tracking Evaluation
    if (this.currentMode === 'tracking') {
      const intersect = this.shootingSystem.evaluateTrackingIntersection();
      if (intersect) {
        this.metrics.timeOnTargetMs += delta * 1000;
        this.metrics.score += Math.round(delta * 220); // Steady point stream while tracking
      }
      const elapsedTotalMs = (this.roundDuration - this.timeRemaining) * 1000;
      if (elapsedTotalMs > 0) {
        this.metrics.trackingPct = Math.min(100, Math.round((this.metrics.timeOnTargetMs / elapsedTotalMs) * 1000) / 10);
      }
    }

    // 3. Reaction Cue Timer
    if (this.currentMode === 'reaction' && this.reactionState === 'waiting_cue') {
      this.reactionWaitTimer -= delta;
      if (this.reactionWaitTimer <= 0) {
        this.triggerReactionCue();
      }
    }

    // 4. Adaptive Message Fade
    if (this.adaptiveMsgTimer > 0) {
      this.adaptiveMsgTimer -= delta;
      if (this.adaptiveMsgTimer <= 0) {
        this.adaptiveMessage = '';
      }
    }

    this.notifyTick();
  }

  finishRound() {
    this.state = 'finished';
    this.audioManager.playRoundComplete();
    this.targetManager.clear();

    const isArabic = document.documentElement.lang !== 'en';
    const avgAcquisition = this.metrics.acquisitionTimes.length > 0
      ? this.metrics.acquisitionTimes.reduce((a, b) => a + b, 0) / this.metrics.acquisitionTimes.length
      : 0;
    const avgReaction = this.metrics.reactionTimes.length > 0
      ? this.metrics.reactionTimes.reduce((a, b) => a + b, 0) / this.metrics.reactionTimes.length
      : null;

    const sessionRecord = {
      mode: this.currentMode,
      gamePreset: this.settingsStore.get('selectedGame'),
      score: this.metrics.score,
      accuracy: this.metrics.accuracy,
      hits: this.metrics.hits,
      shots: this.metrics.shots,
      reactionMs: avgReaction,
      avgAcquisitionMs: avgAcquisition,
      trackingPct: this.metrics.trackingPct,
      falseStarts: this.metrics.falseStarts,
      durationSec: this.roundDuration
    };

    // Save to local history
    const savedEntry = this.sessionHistory.addSession(sessionRecord);

    trackEvent('tool_completed', 'aim-assist-training', {
      score: this.metrics.score,
      accuracy: this.metrics.accuracy,
      mode: this.currentMode
    });

    // Analyze performance & coaching tips
    const coaching = analyzeSessionPerformance(sessionRecord, isArabic);

    if (this.onFinished) {
      this.onFinished({
        session: savedEntry,
        coaching,
        isNewBest: savedEntry.isNewBest
      });
    }

    this.notifyState();
  }

  notifyState() {
    if (this.onStateChange) this.onStateChange(this.state);
  }

  notifyTick() {
    if (this.onTick) this.onTick(this);
  }
}
